import React from 'react';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, AlertTriangle, CheckCircle, Info } from 'lucide-react';
import { AppError, ErrorType } from '@/utils/error/errorHandler';

export type HighlightLogContext = 'admin' | 'generate';

export interface HighlightGenerationStatusProps {
  /**
   * The current status of highlight generation
   */
  status: 'idle' | 'loading' | 'success' | 'error';
  
  /**
   * The error object if status is 'error'
   */
  error?: Error | AppError | null;
  
  /**
   * The context in which this component is used
   */
  context: HighlightLogContext;
  
  /**
   * The number of highlights successfully generated
   */
  highlightCount?: number;
  
  /**
   * Callback to retry highlight generation
   */
  onRetry?: () => void;
  
  /**
   * Callback to dismiss the status message
   */
  onDismiss?: () => void;
}

/**
 * Component that displays highlight generation status with context-awareness
 * Adapts the level of detail and UI based on whether it's in the admin or generate page
 */
const HighlightGenerationStatus: React.FC<HighlightGenerationStatusProps> = ({
  status,
  error,
  context,
  highlightCount,
  onRetry,
  onDismiss
}) => {
  // Don't show anything in idle status
  if (status === 'idle') return null;
  
  // Different UI for loading state based on context
  if (status === 'loading') {
    return (
      <Alert variant="default" className="bg-muted">
        <div className="flex items-center">
          <div className="mr-2 animate-spin">
            <AlertCircle className="h-4 w-4" />
          </div>
          <AlertTitle>
            {context === 'admin' 
              ? 'Generating Highlights...' 
              : 'Creating Style Highlights...'}
          </AlertTitle>
        </div>
        <AlertDescription className="mt-2">
          {context === 'admin'
            ? 'Using AI to analyze the poem and generate appropriate highlights. This may take a moment...'
            : 'Analyzing the poem style to highlight key features...'}
        </AlertDescription>
      </Alert>
    );
  }
  
  // Success state with count of highlights
  if (status === 'success') {
    return (
      <Alert variant="default" className="bg-green-50 border-green-200">
        <div className="flex items-center">
          <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
          <AlertTitle className="text-green-800">
            {context === 'admin' 
              ? 'Highlights Generated Successfully' 
              : 'Style Analysis Complete'}
          </AlertTitle>
          {onDismiss && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onDismiss}
              className="ml-auto text-green-700 hover:text-green-800 hover:bg-green-100"
            >
              Dismiss
            </Button>
          )}
        </div>
        <AlertDescription className="mt-2 text-green-700">
          {highlightCount !== undefined && (
            <span className="flex items-center">
              Generated {highlightCount} highlight{highlightCount !== 1 ? 's' : ''}.
              {context === 'admin' && highlightCount > 0 && (
                <Badge variant="outline" className="ml-2 bg-green-100 text-green-800 border-green-300">
                  {highlightCount} highlight{highlightCount !== 1 ? 's' : ''}
                </Badge>
              )}
            </span>
          )}
        </AlertDescription>
      </Alert>
    );
  }
  
  // Error state with appropriate detail level based on context
  if (status === 'error' && error) {
    // Get error details
    const appError = error instanceof AppError ? error : null;
    const errorType = appError?.type || ErrorType.UNKNOWN;
    const errorMessage = error.message || 'An unknown error occurred during highlight generation';
    
    // Different styling based on severity
    const isCritical = errorType === ErrorType.UNKNOWN || 
                      errorType === ErrorType.NETWORK ||
                      errorType === ErrorType.DATABASE;
    
    return (
      <Alert variant="destructive" className={isCritical ? "bg-red-50 border-red-200" : "bg-amber-50 border-amber-200"}>
        <div className="flex items-center">
          {isCritical 
            ? <AlertCircle className="h-4 w-4 text-red-600 mr-2" /> 
            : <AlertTriangle className="h-4 w-4 text-amber-600 mr-2" />
          }
          <AlertTitle className={isCritical ? "text-red-800" : "text-amber-800"}>
            {context === 'admin' 
              ? 'Error Generating Highlights' 
              : 'Style Analysis Issue'}
          </AlertTitle>
          {onDismiss && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onDismiss}
              className={isCritical 
                ? "ml-auto text-red-700 hover:text-red-800 hover:bg-red-100"
                : "ml-auto text-amber-700 hover:text-amber-800 hover:bg-amber-100"
              }
            >
              Dismiss
            </Button>
          )}
        </div>
        <AlertDescription className={`mt-2 ${isCritical ? "text-red-700" : "text-amber-700"}`}>
          {/* Admin context shows more detailed error information */}
          {context === 'admin' ? (
            <div className="space-y-2">
              <p>{errorMessage}</p>
              {appError && (
                <div className="text-sm">
                  <span className="font-medium">Error Type:</span> {errorType}
                  {appError.code && (
                    <span className="ml-2">
                      <span className="font-medium">Code:</span> {appError.code}
                    </span>
                  )}
                </div>
              )}
            </div>
          ) : (
            // Generate context shows simplified error message
            <p>{getSimplifiedErrorMessage(error)}</p>
          )}
          
          {onRetry && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={onRetry}
              className="mt-2"
            >
              Retry
            </Button>
          )}
        </AlertDescription>
      </Alert>
    );
  }
  
  return null;
};

/**
 * Gets a user-friendly error message for the generate page context
 * @param error - The error object
 * @returns A simplified error message
 */
function getSimplifiedErrorMessage(error: Error | AppError): string {
  const message = error.message.toLowerCase();
  
  if (message.includes('validation') || message.includes('invalid')) {
    return 'The poem structure could not be properly analyzed. Your poem has been saved, but style highlights may be incomplete.';
  }
  
  if (message.includes('network') || message.includes('connection') || message.includes('timeout')) {
    return 'Connection issues prevented style analysis. Your poem has been saved, but style highlights could not be generated.';
  }
  
  if (message.includes('no highlights') || message.includes('empty response') || message.includes('no data')) {
    return 'Style analysis could not identify highlight-worthy elements in your poem. The poem has been saved without style highlights.';
  }
  
  // Default fallback message
  return 'An issue occurred during style analysis. Your poem has been saved, but style highlights may be incomplete.';
}

export default HighlightGenerationStatus; 